type Entry = { at: number; value: unknown };

const inflight = new Map<string, Promise<unknown>>();
const fresh = new Map<string, Entry>();
/** Bumped on invalidate so an in-flight GET cannot write the value back. */
const generation = new Map<string, number>();

/**
 * Boot window for a successful GET. Callers that start a moment apart (the
 * machine guard, then the path gate) still share one response.
 */
export const COALESCE_TTL_MS = 4000;

export type CoalesceOptions<T> = {
  /** `0` shares only the in-flight promise, so a later retry hits the network. */
  ttlMs?: number;
  /** Resolved values that fail this check are not stored. Defaults to all. */
  cacheIf?: (value: T) => boolean;
};

/**
 * Concurrent GETs of the same key share one request. A rejection is never
 * stored, so the next caller tries again.
 */
export function coalesceGet<T>(
  key: string,
  run: () => Promise<T>,
  options?: CoalesceOptions<T>,
): Promise<T> {
  const ttlMs = options?.ttlMs ?? COALESCE_TTL_MS;
  const seen = generation.get(key) ?? 0;
  if (ttlMs > 0) {
    const cached = fresh.get(key);
    if (cached && Date.now() - cached.at < ttlMs) {
      return Promise.resolve(cached.value as T);
    }
  }

  const pending = inflight.get(key);
  if (pending) return pending as Promise<T>;

  const cacheIf = options?.cacheIf ?? (() => true);
  const promise = run()
    .then((value) => {
      if (ttlMs > 0 && cacheIf(value) && (generation.get(key) ?? 0) === seen) {
        fresh.set(key, { at: Date.now(), value });
      }
      return value;
    })
    .finally(() => {
      if (inflight.get(key) === promise) inflight.delete(key);
    });

  inflight.set(key, promise);
  return promise;
}

export function invalidateCoalesced(key: string): void {
  generation.set(key, (generation.get(key) ?? 0) + 1);
  inflight.delete(key);
  fresh.delete(key);
}

/**
 * Drop cached reads before the write and again after it. A GET that starts
 * during the write must not be stored as the post-write body.
 */
export async function invalidateAroundWrite<T>(keys: string[], write: () => Promise<T>): Promise<T> {
  const drop = () => {
    for (const key of keys) invalidateCoalesced(key);
  };
  drop();
  try {
    return await write();
  } finally {
    drop();
  }
}

/** Test helper. */
export function clearCoalescedForTests(): void {
  inflight.clear();
  fresh.clear();
  generation.clear();
}
